import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { api } from '@/runtime'
import type { Host, HostDisk, HostsSummary } from '@/db/api'
import { statusDot, statusText, useBar } from '@/lib/colour'
import {
  CPU_HIGH,
  LOAD_HIGH,
  MEMORY_HIGH,
  byEstate,
  diskLevel,
  fmtLoad,
  fmtUptime,
  hostLevel,
  hostsToLook,
  pct,
  roleWords,
  shareLevel,
  type Level,
} from '@/lib/hosts'
import { pctOf, shareOf } from '@/lib/storage'
import { Skeleton } from './Skeleton'
import { Failed, SectionTitle, Tag, cx } from './ui'

/**
 * Hosts, the infra pack's machines (2026-10-31): every home host and cluster
 * node from GET /api/hosts (internal/hosts, all from Prometheus). `HostsList`
 * is the Hosts page (/infra/hosts), grouped by estate, one row per machine;
 * `HostsGlance` the Overview's short part: what needs a look, or one calm
 * line. Online only: Skeletons on the first read, Failed when it fails, the
 * last answer kept through a later failure (useOnline).
 */

export const loadHosts = () => api.hosts()

export interface HostsState {
  data: HostsSummary | null
  error: string
  reload: () => void
}

// rows on the Overview before "And more"
const GLANCE_CAP = 5

const Dot = ({ level, label }: { level: Level | 'unknown'; label?: string }) => (
  <span
    className={cx('h-2 w-2 rounded-full shrink-0', statusDot[level])}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  />
)

/** One figure in the row's strip: its name small, its value coloured past its line. */
const Stat = ({
  label,
  value,
  level,
  disk,
}: {
  label: string
  value: string
  level: Level
  /** the disk's use bar under its figure */
  disk?: HostDisk
}) => (
  <div className="min-w-0">
    <p className="text-[11px] text-faint leading-4">{label}</p>
    <p className={cx('text-[13px] leading-5 tabular truncate', statusText[level])}>{value}</p>
    {disk && (
      <div
        className={cx('h-1.5 rounded-full overflow-hidden mt-1', useBar.track)}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pctOf(shareOf(disk))}
        aria-label={`${disk.mount}, ${pctOf(shareOf(disk))}% full`}
      >
        <div
          className={cx('h-full rounded-full', useBar[diskLevel(disk)])}
          style={{ width: `${Math.max(1, Math.round(shareOf(disk) * 100))}%` }}
        />
      </div>
    )}
  </div>
)

const HostRow = ({ host: h }: { host: Host }) => {
  const level = hostLevel(h)
  const disk = h.root ?? h.worst
  const line = [
    h.note,
    // "Up" opens the line when there is no note (a region keeps its own case)
    h.up && h.uptime ? `${h.note ? 'up' : 'Up'} ${fmtUptime(h.uptime)}` : '',
    h.up && h.cores ? (h.cores === 1 ? '1 core' : `${h.cores} cores`) : '',
  ]
    .filter(Boolean)
    .join(', ')
  return (
    <li className="px-3 py-2.5" data-testid="host-row">
      <div className="flex items-center gap-2 min-h-6">
        <Dot level={h.up ? level : 'critical'} label={h.up ? undefined : 'not reporting'} />
        <p className="text-[15px] leading-5 font-medium break-words min-w-0">{h.name}</p>
        <Tag>{roleWords(h.role)}</Tag>
        {!h.up && <Tag tone="warn">Not reporting</Tag>}
        {h.reboot_required && <Tag tone="warn">Reboot waiting</Tag>}
      </div>
      <div className="pl-4">
        {line && <p className="text-[12px] text-muted leading-snug mt-0.5">{line}</p>}
        {!h.up ? (
          <p className="text-[13px] text-muted leading-snug mt-1">
            Its node exporter does not answer, so nothing else is known.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <Stat
                label="CPU"
                value={h.cpu === undefined ? 'n/a' : `${pct(h.cpu)}%`}
                level={shareLevel(h.cpu, CPU_HIGH)}
              />
              <Stat
                label="Memory"
                value={h.memory === undefined ? 'n/a' : `${pct(h.memory)}%`}
                level={shareLevel(h.memory, MEMORY_HIGH)}
              />
              <Stat
                label="Load"
                value={h.load === undefined ? 'n/a' : fmtLoad(h.load)}
                level={h.load !== undefined && h.load >= LOAD_HIGH ? 'warn' : 'ok'}
              />
              <Stat
                label="Disk"
                value={disk ? `${pctOf(shareOf(disk))}%` : 'n/a'}
                level={disk ? diskLevel(disk) : 'ok'}
                disk={disk}
              />
            </div>
            {(h.worst && h.worst.mount !== disk?.mount) || h.smart ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-faint">
                {h.worst && h.worst.mount !== disk?.mount && (
                  <span className={diskLevel(h.worst) === 'ok' ? undefined : statusText[diskLevel(h.worst)]}>
                    Fullest <span className="font-mono">{h.worst.mount}</span> {pctOf(shareOf(h.worst))}%
                  </span>
                )}
                {h.smart && (
                  <span
                    className="inline-flex items-center gap-1"
                    aria-label={`SMART ${h.smart === 'ok' ? 'passes' : h.smart === 'failing' ? 'fails' : 'not read'}`}
                  >
                    <Dot level={h.smart === 'ok' ? 'ok' : h.smart === 'failing' ? 'critical' : 'unknown'} />
                    SMART
                    {h.smart === 'failing' && h.smart_failing?.length
                      ? ` fails on ${h.smart_failing.join(', ')}`
                      : ''}
                    {h.smart === 'unknown' ? ' not read' : ''}
                  </span>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </li>
  )
}

const Loading = ({ rows }: { rows: number }) => (
  <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading" data-testid="hosts-loading">
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} className={i === 0 ? 'h-40' : 'h-24'} />
    ))}
  </div>
)

/** The Hosts page's body: every machine, grouped by estate. */
export const HostsList = ({ data, error, reload }: HostsState) => {
  if (!data) return error ? <Failed what="the hosts" reason={error} onRetry={reload} /> : <Loading rows={3} />
  const groups = byEstate(data.hosts)
  if (!groups.length)
    return (
      <p className="card px-3 py-3 text-sm text-muted" data-testid="hosts-none">
        Prometheus knows of no machine yet.
      </p>
    )
  return (
    <>
      {groups.map((g) => (
        <div key={g.estate} className="mb-4" data-testid={`hosts-${g.estate}`}>
          <SectionTitle>{g.name}</SectionTitle>
          <ul className="group-list">
            {g.hosts.map((h) => (
              <HostRow key={h.name} host={h} />
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

const allHosts = (
  <Link
    to="/infra/hosts"
    className="text-[13px] text-accent-text inline-flex items-center gap-0.5 min-h-11"
    data-testid="hosts-link"
  >
    Every host
    <ChevronRight size={14} aria-hidden />
  </Link>
)

/** The Overview's Hosts part: the machines that need a look, worst first, or one calm line. */
export const HostsGlance = ({ data, error, reload }: HostsState) => {
  if (!data)
    return error ? <Failed what="the hosts" reason={error} onRetry={reload} /> : <Skeleton className="h-24" />
  const look = hostsToLook(data.hosts)
  if (!look.length)
    return (
      <>
        <p className="card px-3 py-3 text-sm text-muted" data-testid="hosts-calm">
          Every machine answers, with room on its disks and headroom to spare.
        </p>
        {allHosts}
      </>
    )
  return (
    <>
      <ul className="group-list" data-testid="hosts-look">
        {look.slice(0, GLANCE_CAP).map(({ host, issue, level }) => (
          <li key={host.name} className="flex items-start gap-2.5 px-3 py-2.5 min-h-11">
            <span className="flex mt-[6px]">
              <Dot level={level} label={issue.severity} />
            </span>
            <p className="flex-1 min-w-0 text-[14px] leading-5 break-words">{issue.text}</p>
          </li>
        ))}
        {look.length > GLANCE_CAP && (
          <li className="px-3 min-h-10 flex items-center text-[13px] text-faint">And more</li>
        )}
      </ul>
      {allHosts}
    </>
  )
}
